import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import {
  Observable,
  catchError,
  finalize,
  map,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '@environments/environment';
import { UpdateUserDto, User } from 'core/models/user.model';
import { AuthSessionService } from 'core/services/auth-session.service';
import { OneAuthService } from 'core/services/oneauth.service';
import { UserService } from 'core/services/user.service';

export interface UpdatePasswordDto {
  readonly currentPassword: string;
  readonly password: string;
  readonly passwordConfirmation: string;
}

interface PasswordApiResponse {
  ok: boolean;
  message: string;
}

function isUsablePublicId(value: unknown): value is string {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return false;
  }
  const trimmed = String(value).trim();
  return trimmed !== '' && trimmed !== 'null' && trimmed !== 'undefined';
}

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly auth = inject(AuthSessionService);
  private readonly oneAuth = inject(OneAuthService);
  private readonly userService = inject(UserService);
  private readonly http = inject(HttpClient);

  /** Loading próprio - nao compartilhar com UserService.load() de outras telas. */
  readonly loading = signal(false);

  /** Foto escolhida nesta sessão. `null` significa que o usuário excluiu. */
  private readonly localAvatars = signal<
    Readonly<Record<string, string | null>>
  >({});

  avatarUrl(userId: string, remote?: string | null): string | null {
    const local = this.localAvatars();
    if (Object.hasOwn(local, userId)) {
      return local[userId];
    }
    const trimmed = remote?.trim();
    return trimmed ? trimmed : null;
  }

  rememberAvatar(userId: string, url: string | null): void {
    this.localAvatars.update((current) => ({ ...current, [userId]: url }));
  }

  /** Remove override local para voltar a usar `avatar_url` da API. */
  forgetAvatar(userId: string): void {
    this.localAvatars.update((current) => {
      if (!Object.hasOwn(current, userId)) {
        return current;
      }
      const next = { ...current };
      delete next[userId];
      return next;
    });
  }

  fetchMine(options?: { force?: boolean }): Observable<User | null> {
    this.loading.set(true);

    const loadUser = (userId: string): Observable<User | null> =>
      this.userService
        .fetchById(userId, {
          force: options?.force ?? true,
        })
        .pipe(
          tap((user) => {
            if (user && this.auth.user()?.id === user.id) {
              this.syncSession(user);
            }
          }),
        );

    if (environment.useMockAuth) {
      const userId = this.auth.user()?.id;
      if (!isUsablePublicId(userId)) {
        this.loading.set(false);
        return of(null);
      }
      return loadUser(String(userId)).pipe(
        finalize(() => this.loading.set(false)),
      );
    }

    // Reconsulta /auth/me para sincronizar public_id (sessao legada com id numerico/"null").
    return this.oneAuth.fetchProfile().pipe(
      switchMap((response) => {
        const profile = response.data;
        const token = this.oneAuth.getToken();
        if (!token || !profile || !isUsablePublicId(profile.id)) {
          return of(null);
        }

        this.auth.establishFromOneAuthProfile(token, profile);

        return loadUser(String(profile.id));
      }),
      catchError(() => of(null)),
      finalize(() => this.loading.set(false)),
    );
  }

  updateMine(dto: UpdateUserDto): Observable<User> {
    const userId = this.auth.user()?.id;
    if (!isUsablePublicId(userId)) {
      return throwError(() => new Error('Sessão inválida.'));
    }

    return this.userService.update(String(userId), dto).pipe(
      switchMap((updated) => {
        if (!updated) {
          return throwError(
            () => new Error('Não foi possível atualizar seu perfil.'),
          );
        }
        return of(updated);
      }),
    );
  }

  updatePassword(dto: UpdatePasswordDto): Observable<string> {
    return this.http
      .patch<PasswordApiResponse>(`${environment.apiUrl}/auth/password`, {
        current_password: dto.currentPassword,
        password: dto.password,
        password_confirmation: dto.passwordConfirmation,
      })
      .pipe(
        map((response) => response.message || 'Senha atualizada com sucesso.'),
        catchError((error: unknown) =>
          throwError(() => new Error(this.resolvePasswordError(error))),
        ),
      );
  }

  syncSession(user: User): void {
    this.auth.syncCurrentUser(user);
  }

  private resolvePasswordError(error: unknown): string {
    const fallback = 'Não foi possível alterar a senha. Tente novamente.';
    if (!(error instanceof HttpErrorResponse)) {
      return fallback;
    }

    const body = error.error;
    if (typeof body !== 'object' || body === null) {
      return fallback;
    }

    const errors = (body as { errors?: Record<string, unknown> }).errors;
    if (errors) {
      for (const messages of Object.values(errors)) {
        if (typeof messages === 'string' && messages.trim() !== '') {
          return messages;
        }
        if (Array.isArray(messages) && typeof messages[0] === 'string') {
          return messages[0];
        }
      }
    }

    const message = (body as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') {
      return message;
    }

    return fallback;
  }
}
